// client/src/components/AppInitializer.js - Optimiert für MongoDB
import React, { useState, useEffect } from 'react';
import api from '../services/api';
// import './AppInitializer.css'; // Remove CSS import
import { Spin, Result, Button } from 'antd'; // Import Ant Design components

// Diese Komponente stellt sicher, dass die Anwendung korrekt initialisiert ist,
// bevor die App dem Benutzer angezeigt wird
const AppInitializer = ({ children }) => {
    const [initializing, setInitializing] = useState(true);
    const [error, setError] = useState(null);
    const [authReady, setAuthReady] = useState(false);

    useEffect(() => {
        // Hauptinitialisierung der App
        const initializeApp = async () => {
            try {
                console.log('AppInitializer: App-Initialisierung wird gestartet');

                // Health-Check für die API durchführen
                try {
                    const healthResponse = await api.get('/health');
                    if (healthResponse.success) {
                        console.log('AppInitializer: API-Health-Check erfolgreich');
                    } else {
                        console.warn('AppInitializer: API-Health-Check fehlgeschlagen');
                    }
                } catch (healthError) {
                    console.warn('AppInitializer: API-Health-Check-Fehler:', healthError);
                    // Wir setzen trotzdem fort, um lokale Entwicklung zu unterstützen
                }

                // Testshops erstellen, wenn die Datenbank leer ist
                try {
                    const shopsResponse = await api.get('/shops', { params: { limit: 1 }}); // Corrected API call

                    if (!shopsResponse.success || shopsResponse.data.length === 0) {
                        console.log('AppInitializer: Keine Shops gefunden, erstelle Testshops...');

                        // Testshops erstellen
                        const testShops = [
                            {
                                name: 'Test Shop Berlin',
                                ownerName: 'Test Owner',
                                email: 'test@example.com',
                                phone: '+49 123 456789',
                                address: 'Musterstraße 123',
                                postalCode: '10115',
                                city: 'Berlin',
                                country: 'Deutschland',
                                coordinates: {
                                    lat: 52.52,
                                    lng: 13.405
                                },
                                website: 'https://example.com',
                                description: 'Ein Test-Shop für Berlin mit einer guten Auswahl an Snus-Produkten.',
                                openingHours: [
                                    { dayOfWeek: 'Monday', opens: '09:00', closes: '18:00' },
                                    { dayOfWeek: 'Tuesday', opens: '09:00', closes: '18:00' },
                                    { dayOfWeek: 'Wednesday', opens: '09:00', closes: '18:00' },
                                    { dayOfWeek: 'Thursday', opens: '09:00', closes: '18:00' },
                                    { dayOfWeek: 'Friday', opens: '09:00', closes: '18:00' },
                                    { dayOfWeek: 'Saturday', opens: '10:00', closes: '16:00' },
                                    { dayOfWeek: 'Sunday', opens: '', closes: '' },
                                ],
                                brands: ['Epok', 'Lyft', 'Skruf'],
                                subscriptionPlan: 'premium',
                                subscriptionStatus: 'active',
                                isVerified: true,
                                isActive: true
                            },
                            {
                                name: 'Snus Kiosk Hamburg',
                                ownerName: 'Hamburg Owner',
                                email: 'hamburg@example.com',
                                phone: '+49 987 654321',
                                address: 'Hamburger Straße 45',
                                postalCode: '20095',
                                city: 'Hamburg',
                                country: 'Deutschland',
                                coordinates: {
                                    lat: 53.551,
                                    lng: 9.993
                                },
                                website: 'https://hamburg-snus.example.com',
                                description: 'Der beste Snus-Laden in Hamburg mit einer großen Auswahl an skandinavischen Marken.',
                                openingHours: [
                                    { dayOfWeek: 'Monday', opens: '08:00', closes: '20:00' },
                                    { dayOfWeek: 'Tuesday', opens: '08:00', closes: '20:00' },
                                    { dayOfWeek: 'Wednesday', opens: '08:00', closes: '20:00' },
                                    { dayOfWeek: 'Thursday', opens: '08:00', closes: '20:00' },
                                    { dayOfWeek: 'Friday', opens: '08:00', closes: '22:00' },
                                    { dayOfWeek: 'Saturday', opens: '09:00', closes: '22:00' },
                                    { dayOfWeek: 'Sunday', opens: '10:00', closes: '18:00' },
                                ],
                                brands: ['General', 'Thunder', 'Siberia', 'Zyn'],
                                subscriptionPlan: 'basic',
                                subscriptionStatus: 'active',
                                isVerified: false,
                                isActive: true
                            }
                        ];

                        // Need a user to associate the shop with - this part might need adjustment
                        // For now, let's assume a default user or skip user association for test shops
                        const defaultUserId = null; // Or fetch/create a default user ID if necessary

                        for (const shop of testShops) {
                            try {
                                // Add user ID if available
                                const shopData = defaultUserId ? { ...shop, user: defaultUserId } : shop;
                                // Make sure createShop in backend handles potentially missing user or adjust here
                                await api.post('/shops', shopData);
                                console.log(`AppInitializer: Testshop '${shop.name}' erstellt`);
                            } catch (error) {
                                console.error(`AppInitializer: Fehler beim Erstellen des Testshops '${shop.name}':`, error);
                            }
                        }

                        console.log('AppInitializer: Testshops wurden erstellt');
                    } else {
                        console.log('AppInitializer: Shops existieren bereits in der Datenbank');
                    }
                } catch (shopsError) {
                    console.warn('AppInitializer: Fehler beim Prüfen/Erstellen von Testshops:', shopsError);
                }

                // Initialisierung abschließen
                console.log('AppInitializer: App-Initialisierung abgeschlossen');
                setInitializing(false);
                // setAuthReady will be set by checkAuthStatus

            } catch (err) {
                console.error('AppInitializer: Fehler während der Initialisierung:', err);
                setError(`Fehler bei der App-Initialisierung: ${err.message}`);
                setInitializing(false);
                setAuthReady(true); // Set authReady even on error to show error message
            }
        };

        // Aktuellen Authentifizierungsstatus prüfen
        const checkAuthStatus = async () => {
            try {
                const response = await api.get('/auth/me');
                if (response.success) {
                    console.log('AppInitializer: Benutzer ist authentifiziert');
                } else {
                    console.log('AppInitializer: Benutzer ist nicht authentifiziert');
                }
            } catch (error) {
                console.log('AppInitializer: Benutzer ist nicht authentifiziert (Fehler):', error);
            } finally {
                 setAuthReady(true); // Mark auth check as complete regardless of outcome
            }
        };

        // Run both initializations
        initializeApp();
        checkAuthStatus();

    }, []);

    // Wenn es einen kritischen Initialisierungsfehler gibt, diesen anzeigen
    if (error) {
        return (
            // Use Ant Design Result for error state
             <Result
                 status="error"
                 title="Initialisierungsfehler"
                 subTitle={error} // Display the error message
                 extra={[
                    <Button 
                        type="primary" 
                        key="reload" 
                        onClick={() => window.location.reload()}
                    >
                        Seite neu laden
                     </Button>,
                 ]}
                 style={{ paddingTop: '100px' }} // Add some top padding
             />
        );
    }

    // Während der Initialisierung Ladeanzeige anzeigen
    if (initializing || !authReady) {
        return (
            // Use Ant Design Spin for loading state, centered
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                 <Spin size="large" tip="Anwendung wird geladen..." />
            </div>
        );
    }

    // Nach erfolgreicher Initialisierung die Kinder-Komponenten rendern
    return children;
};

export default AppInitializer;